<!-- Special promotion tab — Net X2/X3 pricingLogicAlgorithm editor. Uses the
     same enr-section-card + enr-collapsible chrome as every other edit tab
     (Overview / Pricing / Term / Shelf) so this reads as a sibling card
     instead of a bespoke one. -->

<div class="sp-layout">
  <nav class="sp-sidenav" aria-label="Form sections">
    <span class="sp-sidenav__eyebrow">On this page</span>
    <a
      href="#sp-section-promotion"
      [class.sp-sidenav__item--active]="activeSection() === 'sp-section-promotion'"
      (click)="selectSection($event, 'sp-section-promotion')"
      >Special promotion</a
    >
    <a
      href="#sp-section-insurance"
      [class.sp-sidenav__item--active]="activeSection() === 'sp-section-insurance'"
      (click)="selectSection($event, 'sp-section-insurance')"
      >Insurance</a
    >
  </nav>
  <main class="sp-sections">
    <div
      id="sp-section-promotion"
      class="enr-section-card enr-section-card--readonly"
    >
      <enr-collapsible
        title="Special promotion — Net X2/X3"
        [expanded]="true"
        ariaLabel="Special promotion — Net X2/X3"
      >
        <span slot="title" class="flex items-center gap-2">
          <span class="sp-title-num" aria-hidden="true">1</span>
          <span class="enr-section-card__title"
            >Special promotion — Net X2/X3</span
          >
        </span>

        <div class="sp-card-body">
          @if (selectedPrice(); as price) {
          <form [formGroup]="form" class="flex flex-col gap-4">
            <!-- POP ID + search -->
            <div class="sp-popid-row">
              <enr-input
                label="POP ID (Net X2/X3 package)"
                placeholder="e.g. 1DtzYZHdx77"
                formControlName="popId"
              ></enr-input>
              <enr-button
                variant="primary"
                [leadingIcon]="true"
                [loading]="lookupState() === 'loading'"
                [disabled]="lookupState() === 'loading'"
                (clicked)="onSearch()"
                [size]="'medium'"
              >
                <mat-icon slot="leading-icon">search</mat-icon>
                Search
              </enr-button>
            </div>

            <!-- Everything below the search is revealed only once a POP ID
             resolves to a package (lookupState === "ok"). -->
            @if (lookupState() === "ok") {
            <!-- Resolved package — read-only summary. -->
            <div class="sp-resolved">
              <div class="sp-resolved__badge">
                <mat-icon class="sp-resolved__check" aria-hidden="true"
                  >check_circle</mat-icon
                >
                <span class="sp-resolved__label">Package resolved</span>
                <span class="sp-resolved__ro">Read-only</span>
              </div>
              @if (packageNameRows().length > 0) {
              <dl class="sp-resolved__names">
                @for (row of packageNameRows(); track row.locale) {
                <div class="sp-resolved__name-row">
                  <dt class="sp-resolved__lang">{{ row.label }}</dt>
                  <dd
                    class="sp-resolved__value"
                    [class.sp-resolved__value--empty]="!row.value"
                  >
                    {{ row.value || "—" }}
                  </dd>
                </div>
                }
              </dl>
              } @else {
              <div class="sp-resolved__single">{{ packageName() }}</div>
              }
            </div>

            <!-- Package type -->
            <enr-select
              label="Package type"
              [options]="packageTypeOptions"
              formControlName="packageType"
            ></enr-select>

            <!-- Periods -->
            <div class="sp-section-head">
              <span class="sp-section-title">
                Period
                <span class="sp-count"
                  >{{ periods.length }}/{{ maxPeriods }}</span
                >
              </span>
              <enr-button
                variant="outline"
                [disabled]="periods.length >= maxPeriods"
                (clicked)="addRow()"
              >
                + Create New Date
              </enr-button>
            </div>

            <div formArrayName="periods" class="flex flex-col gap-3">
              @for (group of periods.controls; track $index) {
              <div class="sp-period-row" [formGroupName]="$index">
                <enr-input
                  type="date"
                  label="Start date"
                  valueFormat="string"
                  formControlName="startDate"
                  [minDate]="startMinFor($index)"
                ></enr-input>
                <enr-input
                  type="date"
                  label="End date"
                  valueFormat="string"
                  formControlName="endDate"
                  [minDate]="endMinFor($index)"
                ></enr-input>
                @if (statusFor($index); as st) {
                <enr-status
                  class="sp-status-badge"
                  [status]="statusType($index)"
                  [customText]="st"
                />
                } @else {
                <span class="sp-status">—</span>
                }
                <enr-button-icon
                  ariaLabel="Delete period"
                  (clicked)="removeRow($index)"
                >
                  <mat-icon>delete_outline</mat-icon>
                </enr-button-icon>
              </div>
              } @empty {
              <p class="sp-empty">
                No promotion periods configured. "Create New Date" to add one.
              </p>
              }
            </div>

            <!-- Validation -->
            @if (errors().length > 0) {
            <div class="sp-errors" role="alert">
              @for (err of errors(); track err) {
              <div class="sp-errors__item">{{ err }}</div>
              }
              <div class="sp-hint">
                Fix these to include the changes in the next Save.
              </div>
            </div>
            } } @else if (lookupState() === "notfound") {
            <output class="sp-lookup-msg sp-lookup-msg--notfound">
              <mat-icon class="sp-lookup-msg__icon" aria-hidden="true"
                >search_off</mat-icon
              >
              <div class="sp-lookup-msg__body">
                <div class="sp-lookup-msg__title">Package not found</div>
                <div class="sp-lookup-msg__text">
                  No package matched that POP ID. Check it and search again.
                </div>
              </div>
            </output>
            } @else {
            <output class="sp-lookup-msg">
              <mat-icon class="sp-lookup-msg__icon" aria-hidden="true"
                >travel_explore</mat-icon
              >
              <div class="sp-lookup-msg__body">
                <div class="sp-lookup-msg__title">Search a POP ID to begin</div>
                <div class="sp-lookup-msg__text">
                  Enter the Net X2/X3 POP ID above and Search to load the
                  package, then configure its promotion periods.
                </div>
              </div>
            </output>
            }
          </form>
          } @else {
          <p class="sp-empty">This package has no prices to configure.</p>
          }
        </div>
      </enr-collapsible>
    </div>

    <div
      id="sp-section-insurance"
      class="enr-section-card enr-section-card--readonly"
    >
      <enr-collapsible
        title="Insurance"
        [expanded]="true"
        ariaLabel="Insurance"
      >
        <span slot="title" class="flex items-center gap-2">
          <span class="sp-title-num" aria-hidden="true">2</span>
          <span class="enr-section-card__title">Insurance</span>
        </span>

        <div class="sp-card-body">
          <div class="sp-inner-card">
            <div class="sp-inner-title">Insurance detail</div>
            @if (selectedInsuranceLanguage(); as language) {
            <section class="sp-insurance-lang-card">
              <div class="sp-insurance-lang-card-body">
                <div class="sp-localized-label">
                  <span>{{ language.shortCode }} · Name</span>
                  @for (flag of languages(); track flag.locale) {
                  <button
                    type="button"
                    [class.sp-localized-label__flag--active]="flag.locale === language.locale"
                    (click)="selectInsuranceLanguage(flag.locale)"
                    [attr.aria-label]="'Switch to ' + flag.label"
                  >
                    @if (insuranceFlagSrc(flag.locale); as flagSrc) {
                    <img [src]="flagSrc" [alt]="flag.label" /> } @else { {{
                    flag.shortCode }} }
                  </button>
                  }
                </div>
                <enr-select
                  label=""
                  [searchable]="true"
                  [disabled]="insuranceDetailLoading()"
                  [options]="insuranceOptionsFor(language.locale)"
                  [value]="selectedInsurancePopId()"
                  (valueChange)="selectInsurance($any($event))"
                ></enr-select>
                <div class="sp-localized-label">
                  <span>{{ language.shortCode }} · Description</span>
                  @for (flag of languages(); track flag.locale) {
                  <button
                    type="button"
                    [class.sp-localized-label__flag--active]="flag.locale === language.locale"
                    (click)="selectInsuranceLanguage(flag.locale)"
                    [attr.aria-label]="'Switch to ' + flag.label"
                  >
                    @if (insuranceFlagSrc(flag.locale); as flagSrc) {
                    <img [src]="flagSrc" [alt]="flag.label" /> } @else { {{
                    flag.shortCode }} }
                  </button>
                  }
                </div>
                <pricing-locale-input
                  [value]="insuranceDescription()"
                  [languages]="[language]"
                  [disableViewFilter]="true"
                  [flagGaps]="true"
                  [disabled]="!insuranceDetail() || insuranceDetailLoading()"
                  type="textarea"
                  [placeholder]="'Description in ' + language.label"
                  (valueChange)="onInsuranceDescriptionChange($event)"
                ></pricing-locale-input>
                <enr-select
                  label="Benefit"
                  [disabled]="!insuranceDetail() || insuranceDetailLoading()"
                  [options]="benefitOptionsFor(language.locale)"
                  [value]="selectedBenefit()"
                  (valueChange)="selectBenefit($any($event))"
                ></enr-select>
              </div>
            </section>
            } @else {
            <p class="sp-empty">No configured languages.</p>
            } @if (insuranceLoadError()) {
            <p class="sp-errors__item">{{ insuranceLoadError() }}</p>
            }
          </div>
          <div class="sp-inner-card">
            <div class="sp-inner-title">Period</div>
            <div class="sp-insurance-period">
              <enr-input
                type="date"
                valueFormat="string"
                label="Start date"
                [disabled]="!insuranceDetail()"
                [value]="insuranceStartDate()"
                (valueChange)="insuranceStartDate.set($any($event)); markInsuranceEdit()"
              ></enr-input>
              <enr-input
                type="date"
                valueFormat="string"
                label="End date"
                [disabled]="!insuranceDetail()"
                [value]="insuranceEndDate()"
                (valueChange)="insuranceEndDate.set($any($event)); markInsuranceEdit()"
              ></enr-input>
            </div>
          </div>
          <div class="sp-inner-card">
            <div class="sp-inner-title">Attachment</div>
            <div class="sp-upload-label">Upload image</div>
            <div
              class="sp-attachment-dropzone"
              [class.sp-attachment-dropzone--dragover]="attachmentDragOver()"
              [class.sp-attachment-dropzone--disabled]="!insuranceDetail()"
              (dragover)="onAttachmentDragOver($event)"
              (dragleave)="onAttachmentDragLeave($event)"
              (drop)="onAttachmentDrop($event)"
            >
              <input
                #attachmentImageInput
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                [disabled]="!insuranceDetail()"
                (change)="onAttachmentFileChange($event)"
              />
              @if (attachmentPreviewUrl(); as previewUrl) {
              <div class="sp-attachment-image-wrap">
                <img
                  class="sp-attachment-preview"
                  [src]="previewUrl"
                  [alt]="attachmentImage()?.name || 'Attachment preview'"
                />
                <div class="sp-attachment-actions">
                  <button type="button" (click)="attachmentImageInput.click()">
                    Change Image
                  </button>
                  <button
                    type="button"
                    class="sp-attachment-delete"
                    (click)="clearAttachmentImage()"
                  >
                    Delete Image
                  </button>
                </div>
              </div>
              <p class="sp-attachment-name">{{ attachmentImage()?.name }}</p>
              } @else if (visibleInsuranceLogo(); as logo) {
              <div class="sp-attachment-image-wrap">
                <img
                  class="sp-attachment-preview"
                  [src]="logo.url"
                  [alt]="logo.name || 'Insurance logo'"
                />
                <div class="sp-attachment-actions">
                  <button type="button" (click)="attachmentImageInput.click()">
                    Change Image
                  </button>
                  <button
                    type="button"
                    class="sp-attachment-delete"
                    (click)="clearAttachmentImage()"
                  >
                    Delete Image
                  </button>
                </div>
              </div>
              <p class="sp-attachment-name">
                {{ logo.name || 'Insurance image' }}
              </p>
              } @else {
              <p>Drag your files to start uploading</p>
              <span>OR</span>
              <button type="button" (click)="attachmentImageInput.click()">
                Browse files
              </button>
              }
            </div>
            @if (attachmentError()) {
            <p class="sp-errors__item">{{ attachmentError() }}</p>
            }
            <enr-input
              label="URL (PDF)"
              [disabled]="!insuranceDetail()"
              [value]="attachmentPdfUrl()"
              (valueChange)="attachmentPdfUrl.set($any($event)); markInsuranceEdit()"
            ></enr-input>
          </div>
          <div class="sp-inner-card">
            <div class="sp-inner-title">Agreement</div>
            <enr-select
              label="Terms & Conditions"
              [disabled]="!insuranceDetail()"
              [options]="agreementOptions()"
              [value]="selectedAgreement()"
              (valueChange)="selectedAgreement.set($any($event)); markInsuranceEdit()"
            ></enr-select>
          </div>
        </div>
      </enr-collapsible>
    </div>
  </main>
</div>
